import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize, Subscription } from 'rxjs';

import { AiApiService } from '@app/_services/ai/ai-api.service';
import { AiAskRequest, AiAskResponse, AiConversationTurn } from '@app/_services/ai/ai-api.models';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

export interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  response?: AiAskResponse;
  error?: string;
  timestamp: Date;
  domain?: string;
  intent?: string;
  showRaw?: boolean;
  pendingConfirmation?: boolean;
  originalQuestion?: string;
}

@Component({
    selector: 'app-ai-dialog',
    templateUrl: './ai-dialog.component.html',
    styleUrls: ['./ai-dialog.component.scss'],
    standalone: false
})
export class AiDialogComponent implements OnInit, OnDestroy {
  @ViewChild('chatContainer') chatContainer!: ElementRef;
  @ViewChild('questionInput') questionInput!: ElementRef;

  form!: FormGroup;
  loading = false;
  messages: ChatMessage[] = [];
  isOpen = false;
  isCollapsed = false;

  private sub?: Subscription;

  preferredDomain?: string | null;

  get bearerToken(): string | undefined {
    try {
      const u = JSON.parse(localStorage.getItem('currentUser') || '{}');
      return u?.token || undefined;
    } catch {
      return undefined;
    }
  }

  suggestedQuestions: string[] = [
    'How many reservations do we have this month?',
    'Show me the latest 5 reviews',
    'How many properties do we manage?',
    'List reviews with rating above 4'
  ];

  constructor(
    private fb: FormBuilder,
    private api: AiApiService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      question: ['', [Validators.required, Validators.minLength(3)]]
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  toggle(): void {
    if (!this.isOpen) {
      this.isOpen = true;
      this.isCollapsed = false;
      setTimeout(() => this.questionInput?.nativeElement?.focus(), 150);
    } else if (this.isCollapsed) {
      this.isCollapsed = false;
      setTimeout(() => this.questionInput?.nativeElement?.focus(), 150);
    } else {
      this.isCollapsed = true;
    }
  }

  collapse(): void {
    this.isCollapsed = true;
  }

  close(): void {
    this.isOpen = false;
    this.isCollapsed = false;
  }

  ask(questionOverride?: string): void {
    const question = (questionOverride || String(this.form.value.question || '')).trim();
    if (!question || question.length < 3) {
      this.form.markAllAsTouched();
      return;
    }

    this.messages.push({
      role: 'user',
      text: question,
      timestamp: new Date()
    });

    this.form.patchValue({ question: '' });
    this.loading = true;
    this.scrollToBottom();

    const conversationHistory: AiConversationTurn[] = this.buildConversationHistory();

    const payload: AiAskRequest = {
      question,
      preferredDomain: this.preferredDomain,
      conversationHistory
    };

    this.sub = this.api
      .ask(payload, this.bearerToken)
      .pipe(finalize(() => {
        this.loading = false;
        this.scrollToBottom();
      }))
      .subscribe({
        next: (resp) => {
          if (resp?.requiresWriteConfirmation && resp?.plan) {
            const msg: ChatMessage = {
              role: 'assistant',
              text: 'This action will modify data. Please review the plan below and confirm or cancel.',
              response: resp,
              timestamp: new Date(),
              domain: resp?.domain,
              intent: resp?.intent,
              pendingConfirmation: true,
              originalQuestion: question
            };
            this.messages.push(msg);
            this.scrollToBottom();
            return;
          }

          const msg: ChatMessage = {
            role: 'assistant',
            text: resp?.answer?.text || '',
            response: resp,
            timestamp: new Date(),
            domain: resp?.domain,
            intent: resp?.intent
          };

          if (resp?.error) {
            msg.error = typeof resp.error === 'string' ? resp.error : JSON.stringify(resp.error);
          } else if (resp?.canAnswer === false) {
            msg.error = this.buildFriendlyError(resp);
          }

          this.messages.push(msg);
          this.scrollToBottom();
        },
        error: (err) => {
          const errorText = err?.status === 0
            ? 'Unable to reach the server. Please check that the API is running.'
            : err?.status === 401
            ? 'Your session has expired. Please log in again.'
            : err?.status === 403
            ? 'You do not have permission to use the AI assistant.'
            : err?.error?.message || err?.message || 'An unexpected error occurred. Please try again.';

          this.messages.push({
            role: 'assistant',
            text: '',
            error: errorText,
            timestamp: new Date()
          });
          this.scrollToBottom();
        }
      });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.ask();
    }
  }

  useSuggestion(question: string): void {
    this.ask(question);
  }

  clearChat(): void {
    this.messages = [];
  }

  toggleRaw(msg: ChatMessage): void {
    msg.showRaw = !msg.showRaw;
  }

  pretty(obj: any): string {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  }

  getAnswerText(msg: ChatMessage): string {
    return msg.response?.answer?.text || '';
  }

  getListItems(msg: ChatMessage): any[] {
    const items = msg.response?.answer?.data?.items;
    return Array.isArray(items) ? items : [];
  }

  getSummaryCount(msg: ChatMessage): number | null {
    const c = msg.response?.answer?.data?.summary?.count;
    return typeof c === 'number' ? c : null;
  }

  getItemTitle(item: any): string {
    return item.title || item.name || item.propertyCode || item.confirmationNumber
      || item.reviewId || item.reservationId || item.shortTermRentalId
      || item.contactId || item.orderId || 'Item';
  }

  getItemDescription(item: any): string {
    return item.reviewDesc || item.description || item.notes || item.publicDesc || '';
  }

  getItemMeta(item: any): string[] {
    const meta: string[] = [];
    const dateFields = ['reviewDate', 'checkInDateTime', 'checkOutDateTime', 'createDate', 'startDate', 'endDate'];
    for (const f of dateFields) {
      if (item[f]) {
        meta.push(`${f}: ${this.formatDate(item[f])}`);
      }
    }
    if (item.status != null) meta.push(`status: ${item.status}`);
    if (item.reservationStatus) meta.push(`status: ${item.reservationStatus}`);
    if (item.rating != null) meta.push(`rating: ${item.rating}`);
    if (item.propertyCode) meta.push(`property: ${item.propertyCode}`);
    return meta;
  }

  getItemAuthor(item: any): string {
    const parts = [item.firstName, item.lastName].filter(Boolean);
    if (parts.length) return parts.join(' ');
    if (item.screenName) return item.screenName;
    if (item.emailAddress) return item.emailAddress;
    return '';
  }

  getAnswerKind(msg: ChatMessage): string {
    return msg.response?.answer?.kind || '';
  }

  getPlanSteps(msg: ChatMessage): any[] {
    return msg.response?.plan?.requestPlan?.steps || [];
  }

  confirmWrite(msg: ChatMessage): void {
    if (!msg.originalQuestion) return;
    msg.pendingConfirmation = false;
    msg.text = 'Write confirmed. Executing...';

    const payload: AiAskRequest = {
      question: msg.originalQuestion,
      preferredDomain: msg.domain || undefined,
      confirmWrite: true,
      conversationHistory: this.buildConversationHistory()
    };

    this.loading = true;
    this.scrollToBottom();

    this.sub = this.api
      .ask(payload, this.bearerToken)
      .pipe(finalize(() => {
        this.loading = false;
        this.scrollToBottom();
      }))
      .subscribe({
        next: (resp) => {
          this.messages.push({
            role: 'assistant',
            text: resp?.answer?.text || 'Write operation completed.',
            response: resp,
            timestamp: new Date(),
            domain: resp?.domain,
            intent: resp?.intent
          });
          this.scrollToBottom();
        },
        error: (err) => {
          this.messages.push({
            role: 'assistant',
            text: '',
            error: 'Write failed: ' + (err?.message || 'Unknown error'),
            timestamp: new Date()
          });
          this.scrollToBottom();
        }
      });
  }

  cancelWrite(msg: ChatMessage): void {
    msg.pendingConfirmation = false;
    msg.text = 'Write operation cancelled.';
  }

  downloadCsv(msg: ChatMessage): void {
    const items = this.getListItems(msg);
    if (!items.length) return;

    const replacer = (_key: string, value: any) => value === null ? '' : value;
    const headers = Object.keys(items[0]);
    const csv = [
      headers.join(','),
      ...items.map(row => headers.map(h => JSON.stringify(row[h] ?? '', replacer)).join(','))
    ].join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const domain = msg.domain || 'export';
    FileSaver.saveAs(blob, `${domain}_${Date.now()}.csv`);
  }

  downloadExcel(msg: ChatMessage): void {
    const items = this.getListItems(msg);
    if (!items.length) return;

    const worksheet = XLSX.utils.json_to_sheet(items);
    const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const domain = msg.domain || 'export';
    FileSaver.saveAs(blob, `${domain}_${Date.now()}.xlsx`);
  }

  exportFullDataset(msg: ChatMessage): void {
    if (!msg.response) return;
    const question = this.messages.find(m => m.role === 'user')?.text || '';

    const payload: AiAskRequest = {
      question,
      preferredDomain: msg.domain || undefined,
      maxResults: 500
    };

    this.loading = true;
    this.api.askExport(payload, this.bearerToken, 'excel').subscribe({
      next: (blob: Blob) => {
        const domain = msg.domain || 'export';
        FileSaver.saveAs(blob, `${domain}_full_${Date.now()}.xlsx`);
        this.loading = false;
      },
      error: (err) => {
        this.messages.push({
          role: 'assistant',
          text: '',
          error: 'Export failed: ' + (err?.message || 'Unknown error'),
          timestamp: new Date()
        });
        this.loading = false;
        this.scrollToBottom();
      }
    });
  }

  hasListItems(msg: ChatMessage): boolean {
    return this.getListItems(msg).length > 0;
  }

  private buildConversationHistory(): AiConversationTurn[] {
    const history: AiConversationTurn[] = [];
    const recent = this.messages.slice(-10);
    for (const m of recent) {
      if (m.role === 'user') {
        history.push({ role: 'user', text: m.text });
      } else if (m.role === 'assistant' && m.text) {
        history.push({ role: 'assistant', text: m.text, domain: m.domain });
      }
    }
    return history;
  }

  private buildFriendlyError(resp: AiAskResponse): string {
    if (resp.router?.needsClarification && resp.router?.clarifyingQuestion) {
      return resp.router.clarifyingQuestion;
    }
    if (resp.error) {
      const err = typeof resp.error === 'string' ? resp.error : JSON.stringify(resp.error);
      if (err.includes('validation failed')) {
        return 'I understood your question but the generated query was invalid. Try rephrasing with more specifics.';
      }
      if (err.includes('No capability spec')) {
        return `I don't have access to the "${resp.domain}" domain yet. Try asking about reviews, reservations, or properties.`;
      }
      return err;
    }
    return 'I wasn\'t able to answer that question. Try rephrasing or ask about a specific domain like reviews, reservations, or properties.';
  }

  private formatDate(val: string): string {
    if (!val) return '';
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return val;
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.chatContainer?.nativeElement) {
        this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
      }
    }, 50);
  }
}
